import { z } from "zod";
import { organisationSchema, roleSchema, skillSchema, trainingSchema } from "./catalog.js";
import { personaSchema } from "./people.js";
import { actionCountersSchema, activityEventSchema, employeeSkillProgressSchema } from "./progress.js";
import { historicalCatalogueReferenceSchema } from "./catalogue-management.js";

export const nextActionSchema = z.object({
  kind: z.enum(["assessment", "training", "test", "waiting", "complete"]),
  skillId: z.string().nullable(),
  title: z.string().min(1),
  description: z.string().min(1),
});

export const employeeDashboardSchema = z.object({
  employeeId: z.string().min(1),
  roleId: z.string().min(1),
  established: z.number().int().nonnegative(),
  awaitingReview: z.number().int().nonnegative(),
  toTrain: z.number().int().nonnegative(),
  unassessed: z.number().int().nonnegative(),
  total: z.number().int().positive(),
  progressPercent: z.number().min(0).max(100),
  nextAction: nextActionSchema,
});

export const checklistItemSchema = z.object({
  employeeId: z.string().min(1),
  employeeName: z.string().min(1),
  roleTitle: z.string().min(1),
  skillId: z.string().min(1),
  skillTitle: z.string().min(1),
  evidenceSource: z.string().min(1),
  submittedAt: z.string().datetime(),
  catalogue: historicalCatalogueReferenceSchema.optional(),
});

export const readinessRowSchema = z.object({
  employeeId: z.string().min(1),
  employeeName: z.string().min(1),
  statuses: z.record(z.string(), employeeSkillProgressSchema.shape.status),
});

export const roleReadinessSchema = z.object({
  roleId: z.string().min(1),
  established: z.number().int().nonnegative(),
  pending: z.number().int().nonnegative(),
  toTrain: z.number().int().nonnegative(),
  unassessed: z.number().int().nonnegative(),
  confirmedPercent: z.number().min(0).max(100),
  gapSkillIds: z.array(z.string()),
  pendingGapSkillIds: z.array(z.string()),
  rows: z.array(readinessRowSchema),
});

export const roleMatchSchema = z.object({
  employeeId: z.string().min(1),
  employeeName: z.string().min(1),
  establishedSkillIds: z.array(z.string()),
  pendingSkillIds: z.array(z.string()),
  gapSkillIds: z.array(z.string()),
});

export const prototypeBootstrapSchema = z.object({
  organisation: organisationSchema,
  personas: z.array(personaSchema),
  roles: z.array(roleSchema),
  skills: z.array(skillSchema),
  trainings: z.array(trainingSchema),
  progress: z.array(employeeSkillProgressSchema),
  employeeCounters: z.record(z.string(), actionCountersSchema),
  organisationCounters: actionCountersSchema,
  events: z.array(activityEventSchema),
  themeId: z.enum(["peer-start", "peer-start-warm"]),
  dashboard: employeeDashboardSchema.nullable(),
  checklist: z.array(checklistItemSchema),
  readiness: z.array(roleReadinessSchema),
  roleMatches: z.record(z.string(), z.array(roleMatchSchema)),
});

export type NextAction = z.infer<typeof nextActionSchema>;
export type EmployeeDashboard = z.infer<typeof employeeDashboardSchema>;
export type ChecklistItem = z.infer<typeof checklistItemSchema>;
export type RoleReadiness = z.infer<typeof roleReadinessSchema>;
export type RoleMatch = z.infer<typeof roleMatchSchema>;
export type PrototypeBootstrap = z.infer<typeof prototypeBootstrapSchema>;
